import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../utils/cn';
import styles from './Field.module.css';
import type { FieldErrorLike } from './fieldUtils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: FieldErrorLike | undefined;
  helper?: ReactNode;
  required?: boolean;
  /** When true, the textarea grows with its content (manual resize disabled). */
  autoResize?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, helper, required, id: idProp, className, autoResize = false, value, defaultValue, ...rest },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? `textarea-${reactId}`;
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

  useEffect(() => {
    if (!autoResize || !internalRef.current) return;
    const el = internalRef.current;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [autoResize, value, defaultValue]);

  const errorMsg = error?.message;
  const errorId = errorMsg ? `${id}-error` : undefined;
  const helperId = helper ? `${id}-helper` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn(styles.wrapper, className)}>
      {label !== undefined ? (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <textarea
        ref={internalRef}
        id={id}
        aria-invalid={errorMsg !== undefined || undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        value={value}
        defaultValue={defaultValue}
        className={cn(styles.control, styles.textarea, errorMsg !== undefined && styles.invalid)}
        style={autoResize ? { resize: 'none', overflow: 'hidden' } : undefined}
        {...rest}
      />
      {errorMsg ? (
        <p id={errorId} className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : helper ? (
        <p id={helperId} className={styles.helper}>
          {helper}
        </p>
      ) : null}
    </div>
  );
});
